import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import {
  AimOutlined,
  AppstoreOutlined,
  ControlOutlined,
  FileTextOutlined,
  ReadOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { ASSET_CLASSES, HISTORICAL_SCENARIO, selectors, Session } from 'state';

import {
  benchmarkComponentLines,
  summarizeGroup,
} from '../../../TrainerSession/SessionSettingsShared/helpers';
import { InstrumentSelectionContext } from '../../../TrainerSession/SessionSettings/instrumentSelectionContext';
import { useSelectedInstrumentCount } from '../../../../features/assetCatalogue/SessionCatalogue';

export type BlueprintDenseView =
  | 'general'
  | 'blueprint'
  | 'instruments'
  | 'news'
  | 'benchmark'
  | 'macro';

type Props = {
  active: BlueprintDenseView;
  onSelect: (view: BlueprintDenseView) => void;
  session: Session;
};

/** V2 copy of the dense rail with blueprint-only controls as a first-class view. */
const BlueprintRailV2: React.FC<Props> = ({ active, onSelect, session }) => {
  const bench = summarizeGroup('benchmark', session);
  const benchComponents = benchmarkComponentLines(session);
  const sector = summarizeGroup('sector', session);
  const instrumentSelection = useContext(InstrumentSelectionContext);
  const selectedIds =
    instrumentSelection?.selected ?? session.underlying_assets ?? [];
  const count = useSelectedInstrumentCount(selectedIds);
  const historical = session.scenario_type === HISTORICAL_SCENARIO;

  const pacingValue = historical
    ? session.time_advancement_type === 'predetermined_intervals'
      ? `${session.historical_sleep_time}s. / scheduled dates`
      : `${session.historical_sleep_time}s. / ${session.frequency} ${
          session.frequency === 1 ? 'day' : 'days'
        }`
    : '5min. ticks';
  const leverageOn = [
    session.fx_leverage,
    session.bond_leverage,
    session.equity_leverage,
    session.etf_equities_leverage,
    session.etf_commodities_leverage,
    session.futures_leverage,
  ].some(margin => Math.round((margin ?? 1) * 100) !== 100);

  const playable = useSelector(
    selectors.playableAssetClassesForIds(selectedIds),
  );
  const playableNames = Array.from(
    new Set(
      (playable ?? [])
        .map((assetClass: number) => ASSET_CLASSES[assetClass])
        .filter(Boolean),
    ),
  );

  const rowClass = (view: BlueprintDenseView, extra?: string) =>
    [
      'set-studio__rail-row',
      extra ?? '',
      active === view ? 'set-studio__rail-row--active' : '',
    ]
      .filter(Boolean)
      .join(' ');

  return (
    <div className="set-studio__rail">
      <div className="set-studio__rail-head">
        <span className="set-studio__rail-title">Settings</span>
      </div>
      <div className="set-studio__rail-list">
        <button
          type="button"
          className={rowClass('general', 'set3b-rail-rich')}
          onClick={() => onSelect('general')}
        >
          <span className="set-studio__rail-icon">
            <ControlOutlined />
          </span>
          <span className="set-studio__rail-text">
            <span className="set-studio__rail-name">General Settings</span>
            <span className="set3b-rail-sublist">
              <span className="set3b-rail-subitem set3b-rail-kv">
                <b>Pacing</b>
                <i>{pacingValue}</i>
              </span>
              <span className="set3b-rail-subitem set3b-rail-kv">
                <b>Leverage</b>
                <i>{leverageOn ? 'ON' : 'OFF'}</i>
              </span>
              <span className="set3b-rail-subitem set3b-rail-aclist">
                <i>Metrics</i>
                <i>Allocations</i>
                <i>Other</i>
              </span>
            </span>
          </span>
        </button>

        <button
          type="button"
          className={rowClass('blueprint', 'set3b-rail-rich')}
          onClick={() => onSelect('blueprint')}
        >
          <span className="set-studio__rail-icon">
            <ToolOutlined />
          </span>
          <span className="set-studio__rail-text">
            <span className="set-studio__rail-name">Blueprint Controls</span>
            <span className="set3b-rail-sublist">
              <span className="set3b-rail-subitem set3b-rail-aclist">
                {/* The session performance benchmark's coverage card, named
                    in full: a bare "benchmark" collides with the answer key. */}
                <i>Performance benchmark</i>
                <i>Options pricing</i>
                <i>Player Markets view</i>
                <i>Dev Plan display name</i>
              </span>
            </span>
          </span>
        </button>

        <button
          type="button"
          className={rowClass('instruments', 'set3b-rail-rich')}
          onClick={() => onSelect('instruments')}
        >
          <span className="set-studio__rail-icon">
            <AppstoreOutlined />
          </span>
          <span className="set-studio__rail-text">
            <span className="set-studio__rail-name">Instrument Universe</span>
            <span className="set3b-rail-sublist">
              <span className="set3b-rail-subitem">
                <b>{count} Instruments</b>
              </span>
              {playableNames.length > 0 ? (
                <span className="set3b-rail-subitem set3b-rail-aclist">
                  {playableNames.map((name: string) => (
                    <i key={name}>{name}</i>
                  ))}
                </span>
              ) : null}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={rowClass('benchmark', 'set3b-rail-rich')}
          onClick={() => onSelect('benchmark')}
        >
          <span className="set-studio__rail-icon">
            <AimOutlined />
          </span>
          <span className="set-studio__rail-text">
            {/* Named in full. On a page that also carries the ANSWER KEY
                (the marking scheme), a bare "Benchmark" names two things:
                this row is the session PERFORMANCE benchmark, the index or
                fund composition a trainee's P&L is measured against. */}
            <span className="set-studio__rail-name">Performance benchmark</span>
            <span className="set3b-rail-sublist">
              {benchComponents.length > 0 ? (
                <span className="set3b-rail-subitem set3b-rail-complist">
                  {benchComponents.map(component => (
                    <i key={component.label} className="set3b-rail-compline">
                      <span className="set3b-rail-comppct">
                        {component.pct}
                      </span>
                      <span className="set3b-rail-complabel">
                        {component.label}
                      </span>
                    </i>
                  ))}
                </span>
              ) : (
                <span className="set3b-rail-subitem">
                  <b>Performance benchmark</b>
                  <i>{bench.value}</i>
                </span>
              )}
              {historical ? (
                <span className="set3b-rail-subitem">
                  <b>Alpha monitoring</b>
                  <i>
                    {sector.value}
                    {sector.sub ? ` · ${sector.sub}` : ''}
                  </i>
                </span>
              ) : null}
            </span>
          </span>
        </button>

        {historical ? (
          <button
            type="button"
            className={rowClass('news')}
            onClick={() => onSelect('news')}
          >
            <span className="set-studio__rail-icon">
              <ReadOutlined />
            </span>
            <span className="set-studio__rail-text">
              <span className="set-studio__rail-name">News</span>
              <span className="set3b-rail-sublist">
                <span className="set3b-rail-subitem">
                  Headlines and display names
                </span>
              </span>
            </span>
          </button>
        ) : null}

        {historical ? (
          <button
            type="button"
            className={rowClass('macro', 'set3b-rail-rich')}
            onClick={() => onSelect('macro')}
          >
            <span className="set-studio__rail-icon">
              <FileTextOutlined />
            </span>
            <span className="set-studio__rail-text">
              <span className="set-studio__rail-name">Macro Background</span>
              <span className="set3b-rail-sublist">
                <span className="set3b-rail-subitem">
                  <i>
                    {session.macro_background
                      ? 'Briefing set'
                      : 'No briefing yet'}
                  </i>
                </span>
              </span>
            </span>
          </button>
        ) : null}
      </div>
      <div className="set-studio__rail-foot">Blueprint settings · V2</div>
    </div>
  );
};

export default BlueprintRailV2;
